import { supabase } from "@/integrations/supabase/client";

export interface CachedPage {
  url: string;
  title: string;
  content: string;
  timestamp: number;
  priority: "high" | "medium" | "low";
  type: "page" | "api" | "asset";
  size: number;
}

export interface CacheStats {
  totalSize: number;
  pageCount: number;
  lastUpdate: number;
  quota: number;
  usage: number;
}

class PWACacheManager {
  private readonly CACHE_NAME = "paystreem-pages-v1";
  private readonly API_CACHE_NAME = "paystreem-api-v1";
  private readonly MAX_CACHE_SIZE = 50 * 1024 * 1024; // 50MB
  private readonly BASE_URL = "https://www.paystreem.com"; // Base URL for cache keys
  private readonly HIGH_PRIORITY_PAGES = [
    "/",
    "/dashboard",
    "/profile",
    "/tracks",
    "/favorites",
    "/charts",
  ];

  // Cache a page with its content
  async cachePage(
    url: string,
    content: string,
    title: string = "",
    priority: "high" | "medium" | "low" = "medium",
  ): Promise<void> {
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const cachedPage: CachedPage = {
        url,
        title,
        content,
        timestamp: Date.now(),
        priority,
        type: "page",
        size: new Blob([content]).size,
      };

      // Store as Response object for service worker compatibility
      const response = new Response(JSON.stringify(cachedPage), {
        headers: {
          "Content-Type": "application/json",
          "X-Cache-Type": "page",
          "X-Cache-Priority": priority,
        },
      });

      await cache.put(new URL(`/page${url}`, this.BASE_URL).href, response);
      await this.cleanupIfNeeded();

      console.log(`✅ Cached page: ${url} (${priority} priority)`);
    } catch (error) {
      console.error("❌ Failed to cache page:", error);
    }
  }

  // Get cached page
  async getCachedPage(url: string): Promise<CachedPage | null> {
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const response = await cache.match(
        new URL(`/page${url}`, this.BASE_URL).href,
      );

      if (response) {
        const cachedPage: CachedPage = await response.json();
        return cachedPage;
      }

      return null;
    } catch (error) {
      console.error("❌ Failed to get cached page:", error);
      return null;
    }
  }

  // Get all cached pages
  async getAllCachedPages(): Promise<CachedPage[]> {
    try {
      const cache = await caches.open(this.CACHE_NAME);
      const requests = await cache.keys();
      const pages: CachedPage[] = [];

      for (const request of requests) {
        if (request.url.includes("/page/")) {
          const response = await cache.match(request);
          if (response) {
            const cachedPage: CachedPage = await response.json();
            pages.push(cachedPage);
          }
        }
      }

      return pages.sort((a, b) => {
        // Sort by priority first, then by timestamp
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        }
        return b.timestamp - a.timestamp;
      });
    } catch (error) {
      console.error("❌ Failed to get cached pages:", error);
      return [];
    }
  }

  // Cache user's favorite tracks and profiles for offline access
  async cacheUserContent(userId: string): Promise<void> {
    try {
      // Cache user profile
      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (profile) {
        await this.cacheAPIResponse(`/api/profile/${userId}`, profile, "high");
      }

      // Cache user's favorite tracks (simplified approach to avoid type issues)
      const { data: favorites } = await supabase
        .from("favorites")
        .select("track_id")
        .eq("user_id", userId);

      if (favorites && favorites.length > 0) {
        await this.cacheAPIResponse(
          `/api/favorites/${userId}`,
          favorites,
          "high",
        );

        // Cache individual track data
        for (const fav of favorites) {
          try {
            const { data: track } = await supabase
              .from("tracks")
              .select("*")
              .eq("id", fav.track_id)
              .single();

            if (track) {
              await this.cacheAPIResponse(
                `/api/track/${track.id}`,
                track,
                "medium",
              );
            }
          } catch (trackError) {
            console.warn(`Could not cache track ${fav.track_id}:`, trackError);
          }
        }
      }

      console.log(`✅ Cached user content for ${userId}`);
    } catch (error) {
      console.error("❌ Failed to cache user content:", error);
    }
  }

  // Cache API response
  async cacheAPIResponse(
    endpoint: string,
    data: any,
    priority: "high" | "medium" | "low" = "medium",
  ): Promise<void> {
    try {
      const cache = await caches.open(this.API_CACHE_NAME);
      const cachedData = {
        endpoint,
        data,
        timestamp: Date.now(),
        priority,
        type: "api" as const,
        size: new Blob([JSON.stringify(data)]).size,
      };

      const response = new Response(JSON.stringify(cachedData), {
        headers: {
          "Content-Type": "application/json",
          "X-Cache-Type": "api",
          "X-Cache-Priority": priority,
        },
      });

      await cache.put(new URL(`/api${endpoint}`, this.BASE_URL).href, response);
      console.log(`✅ Cached API response: ${endpoint}`);
    } catch (error) {
      console.error("❌ Failed to cache API response:", error);
    }
  }

  // Get cached API response
  async getCachedAPIResponse(endpoint: string): Promise<any | null> {
    try {
      const cache = await caches.open(this.API_CACHE_NAME);
      const response = await cache.match(
        new URL(`/api${endpoint}`, this.BASE_URL).href,
      );

      if (response) {
        const cachedData = await response.json();
        return cachedData.data;
      }

      return null;
    } catch (error) {
      console.error("❌ Failed to get cached API response:", error);
      return null;
    }
  }

  // Pre-cache critical pages based on user role
  async preCacheCriticalPages(userRole?: string): Promise<void> {
    const pagesToCache = [...this.HIGH_PRIORITY_PAGES];

    // Add role-specific pages
    if (userRole === "artist") {
      pagesToCache.push("/dashboard/artist", "/upload", "/analytics");
    } else if (userRole === "sponsor") {
      pagesToCache.push("/dashboard/sponsor", "/campaigns");
    } else if (userRole === "admin") {
      pagesToCache.push("/dashboard/admin", "/users", "/reports");
    }

    for (const url of pagesToCache) {
      try {
        // Fetch page content (this would need to be implemented based on your routing)
        const response = await fetch(url);
        if (response.ok) {
          const content = await response.text();
          await this.cachePage(url, content, `${url} - PayStreem`, "high");
        }
      } catch (error) {
        console.warn(`⚠️ Could not pre-cache page: ${url}`);
      }
    }
  }

  // Get cache statistics
  async getCacheStats(): Promise<CacheStats> {
    try {
      const [pageCache, apiCache] = await Promise.all([
        caches.open(this.CACHE_NAME),
        caches.open(this.API_CACHE_NAME),
      ]);

      const [pageRequests, apiRequests] = await Promise.all([
        pageCache.keys(),
        apiCache.keys(),
      ]);

      let totalSize = 0;
      let pageCount = 0;
      let lastUpdate = 0;

      // Calculate total size and get last update
      for (const request of [...pageRequests, ...apiRequests]) {
        const cache = request.url.includes("/page/") ? pageCache : apiCache;
        const response = await cache.match(request);
        if (response) {
          const data = await response.json();
          totalSize += data.size || 0;
          pageCount++;
          lastUpdate = Math.max(lastUpdate, data.timestamp || 0);
        }
      }

      // Get storage quota
      let quota = 0;
      let usage = 0;
      if ("storage" in navigator && "estimate" in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        quota = estimate.quota || 0;
        usage = estimate.usage || 0;
      }

      return {
        totalSize,
        pageCount,
        lastUpdate,
        quota,
        usage,
      };
    } catch (error) {
      console.error("❌ Failed to get cache stats:", error);
      return {
        totalSize: 0,
        pageCount: 0,
        lastUpdate: 0,
        quota: 0,
        usage: 0,
      };
    }
  }

  // Clean up cache when it gets too large
  private async cleanupIfNeeded(): Promise<void> {
    const stats = await this.getCacheStats();

    if (stats.totalSize > this.MAX_CACHE_SIZE) {
      console.log("🧹 Cache size exceeded, cleaning up...");

      const pages = await this.getAllCachedPages();
      const lowPriorityPages = pages.filter((p) => p.priority === "low");

      // Remove oldest low priority pages first
      const sortedLowPriority = lowPriorityPages.sort(
        (a, b) => a.timestamp - b.timestamp,
      );
      const cache = await caches.open(this.CACHE_NAME);

      for (const page of sortedLowPriority.slice(
        0,
        Math.ceil(sortedLowPriority.length / 2),
      )) {
        await cache.delete(new URL(`/page${page.url}`, this.BASE_URL).href);
        console.log(`🗑️ Removed cached page: ${page.url}`);
      }
    }
  }

  // Clear all cached pages
  async clearPageCache(): Promise<void> {
    try {
      await caches.delete(this.CACHE_NAME);
      await caches.delete(this.API_CACHE_NAME);
      console.log("✅ All page cache cleared");
    } catch (error) {
      console.error("❌ Failed to clear page cache:", error);
    }
  }

  // Check if page is cached
  async isPageCached(url: string): Promise<boolean> {
    const page = await this.getCachedPage(url);
    return page !== null;
  }
}

// Export singleton instance
export const pwaCacheManager = new PWACacheManager();
